// src/pages/DischargeSummary.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  addDoc,
  serverTimestamp,
  orderBy,
} from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import { useUserProfile } from "../contexts/UserProfileContext";

import Header from "../components/Header";
import jsPDF from "jspdf";
import {
  ArrowLeft,
  Sparkles,
  Save,
  Copy,
  FileDown,
  CheckCircle,
} from "lucide-react";

import lumosyeLogo from "../assets/Lumosye-logo.png";
import lumochartLogo from "../assets/lumochart-login.png";

import { apiPost } from "../utils/api"; // ✅ unified versioned backend helper

export default function DischargeSummary() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { profile } = useUserProfile();

  const [patient, setPatient] = useState(null);
  const [generatedSummary, setGeneratedSummary] = useState("");
  const [existingSummaryNoteId, setExistingSummaryNoteId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // -----------------------------------------------------
  // Fetch patient + existing discharge note
  // -----------------------------------------------------
  const fetchData = useCallback(async () => {
    if (!patientId) return;

    setIsLoading(true);

    try {
      const pRef = doc(db, "patients", patientId);
      const snap = await getDoc(pRef);
      if (snap.exists()) setPatient(snap.data());

      const notesQuery = query(
        collection(db, "notes"),
        where("patientID", "==", patientId),
        where("type", "==", "Discharge")
      );

      const notesSnap = await getDocs(notesQuery);

      if (!notesSnap.empty) {
        const d = notesSnap.docs[0];
        setExistingSummaryNoteId(d.id);
        setGeneratedSummary(d.data().summary || "");
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message);
    }

    setIsLoading(false);
  }, [patientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // -----------------------------------------------------
  // Generate discharge summary → /v1/generate-discharge-summary
  // -----------------------------------------------------
  const handleGenerateSummary = async () => {
    setIsGenerating(true);
    setErrorMsg("");

    try {
      const allNotesQuery = query(
        collection(db, "notes"),
        where("patientID", "==", patientId),
        orderBy("timestamp", "asc")
      );

      const snap = await getDocs(allNotesQuery);

      const notes = snap.docs.map((d) => {
        const n = d.data();

        return {
          ...n,
          timestamp:
            n.timestamp?.toDate?.().toISOString?.() ||
            new Date().toISOString(),
        };
      });

      if (notes.length === 0) {
        alert("No notes available for this patient.");
        setIsGenerating(false);
        return;
      }

      const data = await apiPost("/generate-discharge-summary", {
        notes,
      });

      setGeneratedSummary(data.summary || "");
    } catch (err) {
      console.error("Generate error:", err);
      setGeneratedSummary(`Error: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // -----------------------------------------------------
  // Save discharge summary note
  // -----------------------------------------------------
  const handleSaveSummary = async () => {
    if (!generatedSummary.trim()) return;

    setIsSaving(true);
    setIsSaved(false);

    const payload = {
      ownerId: currentUser.uid,
      patientID: patientId,
      type: "Discharge",
      summary: generatedSummary,
      timestamp: serverTimestamp(),
    };

    try {
      if (existingSummaryNoteId) {
        await setDoc(doc(db, "notes", existingSummaryNoteId), payload, {
          merge: true,
        });
      } else {
        const ref = await addDoc(collection(db, "notes"), payload);
        setExistingSummaryNoteId(ref.id);
      }

      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 1500);
    } catch (err) {
      console.error("Save error:", err);
      setErrorMsg(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // -----------------------------------------------------
  // Export PDF
  // -----------------------------------------------------
  const exportSummaryAsPDF = () => {
    if (!generatedSummary || !profile || !patient)
      return alert("Missing required data.");

    const filename = `LumoChart_DischargeSummary_${patient.lastName}.pdf`;

    const docPDF = new jsPDF("p", "pt", "letter");
    const margin = 40;
    const width = docPDF.internal.pageSize.getWidth();
    const height = docPDF.internal.pageSize.getHeight();
    const contentWidth = width - margin * 2;

    let y = margin;

    // Logos
    const ratio = 3226 / 825;
    const logoW = 100;
    const logoH = logoW / ratio;
    docPDF.addImage(lumosyeLogo, "PNG", margin, y, logoW, logoH);

    docPDF.addImage(lumochartLogo, "PNG", width - margin - 40, y, 40, 40);

    y += Math.max(logoH, 40) + 15;

    docPDF.setDrawColor(220);
    docPDF.line(margin, y, width - margin, y);
    y += 25;

    docPDF
      .setFont("helvetica", "bold")
      .setFontSize(20)
      .text("Discharge Summary", width / 2, y, { align: "center" });
    y += 30;

    const fullName = `${patient.firstName} ${patient.lastName}`.trim();
    docPDF
      .setFont("helvetica", "normal")
      .setFontSize(12)
      .text(`Patient: ${fullName}`, margin, y);

    docPDF.text(
      `Date: ${new Date().toLocaleString()}`,
      width - margin - 160,
      y
    );
    y += 35;

    // Body
    docPDF.setFontSize(11);
    const lines = docPDF.splitTextToSize(generatedSummary, contentWidth);

    let cursor = y;
    for (let line of lines) {
      if (cursor > height - margin - 20) {
        docPDF.addPage();
        cursor = margin;
      }
      docPDF.text(line, margin, cursor);
      cursor += 14;
    }

    // Footer
    const totalPages = docPDF.internal.getNumberOfPages();

    for (let p = 1; p <= totalPages; p++) {
      docPDF.setPage(p);
      docPDF.setFontSize(9).setTextColor(150);

      docPDF.text(
        `Page ${p} of ${totalPages} | LumoChart`,
        width / 2,
        height - 20,
        { align: "center" }
      );

      if (p === totalPages) {
        docPDF
          .setFont("helvetica", "italic")
          .setFontSize(11)
          .text(
            `Signed electronically by ${profile.fullName}, ${profile.title}`,
            margin,
            height - 40
          );
      }
    }

    docPDF.save(filename);
  };

  // -----------------------------------------------------
  // UI
  // -----------------------------------------------------
  if (isLoading) {
    return (
      <div className="bg-[#f4f7f9] dark:bg-black min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading…</p>
      </div>
    );
  }

  return (
    <div className="bg-[#f4f7f9] dark:bg-black min-h-screen">
      <Header />

      <main className="max-w-4xl mx-auto py-10 px-4">
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-800 mr-4"
          >
            <ArrowLeft size={20} />
          </button>

          <div>
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">
              Discharge Summary
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              for {patient?.firstName} {patient?.lastName}
            </p>
          </div>
        </div>

        {/* ERROR */}
        {errorMsg && (
          <div className="text-red-500 text-sm mb-4">{errorMsg}</div>
        )}

        <div className="space-y-8">
          {/* Generate */}
          <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm p-6 space-y-4">
            <h2 className="text-xl font-bold">
              Generate Summary
            </h2>

            <p className="text-sm text-gray-500 dark:text-gray-400">
              The AI will analyze all patient notes and produce a formatted discharge summary.
            </p>

            <button
              onClick={handleGenerateSummary}
              disabled={isGenerating}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full font-semibold hover:bg-purple-600 disabled:opacity-50"
            >
              {isGenerating ? (
                <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin" />
              ) : (
                <>
                  <Sparkles size={16} /> Generate Summary
                </>
              )}
            </button>

            <textarea
              value={generatedSummary}
              onChange={(e) => setGeneratedSummary(e.target.value)}
              className="w-full h-96 bg-slate-100 dark:bg-zinc-800 rounded-lg p-4 focus:ring-2 focus:ring-blue-500"
              placeholder="AI-generated discharge summary will appear here..."
            />
          </div>

          {/* Save / Export */}
          <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm p-6">
            <h2 className="text-xl font-bold mb-4">Export & Save</h2>

            <div className="grid grid-cols-3 gap-4">
              <button
                onClick={() => navigator.clipboard.writeText(generatedSummary)}
                disabled={!generatedSummary}
                className="py-2 bg-slate-200 dark:bg-zinc-800 rounded-lg hover:bg-slate-300 dark:hover:bg-zinc-700 disabled:opacity-50"
              >
                <Copy size={16} />
              </button>

              <button
                onClick={exportSummaryAsPDF}
                disabled={!generatedSummary}
                className="py-2 bg-slate-200 dark:bg-zinc-800 rounded-lg hover:bg-slate-300 dark:hover:bg-zinc-700 disabled:opacity-50"
              >
                <FileDown size={16} />
              </button>

              <button
                onClick={handleSaveSummary}
                disabled={!generatedSummary || isSaving || isSaved}
                className={`py-2 rounded-lg text-white font-semibold ${
                  isSaved
                    ? "bg-green-500"
                    : "bg-blue-500 hover:bg-blue-600 disabled:opacity-50"
                }`}
              >
                {isSaving ? (
                  <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin" />
                ) : isSaved ? (
                  <CheckCircle size={16} />
                ) : (
                  <Save size={16} />
                )}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
